import type { SignInError } from '../../shared/api';
import { signInUrl } from '../lib/api';

const ERROR_MESSAGES: Record<SignInError, string> = {
  not_configured: "Sign-in hasn't been set up on this server yet (see “Sign-in” in the README).",
  cancelled: 'Sign-in was cancelled.',
  expired: 'The sign-in took too long or was interrupted. Please try again.',
  not_allowed: "That Google account isn't allowed to use this catalogue. Ask its owner to add your email address.",
  unverified_email: "Your Google account's email address isn't verified, so it can't be used to sign in.",
  failed: 'Sign-in failed. Please try again.',
};

function signInError(): string | null {
  const code = new URLSearchParams(window.location.search).get('signin_error');
  return code && code in ERROR_MESSAGES ? ERROR_MESSAGES[code as SignInError] : null;
}

/** Return to the page the person was on, minus the error from a previous attempt. */
function returnPath(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('signin_error');
  return `${url.pathname}${url.search}`;
}

const FEATURES: ReadonlyArray<{ title: string; text: string }> = [
  {
    title: 'Books',
    text: 'Point the camera at the barcode on the back and the title, author, cover and more are filled in. Sort them into shelves of your own: Upstairs, To be read, Loaned…',
  },
  {
    title: 'The bar',
    text: 'Scan bottles and cans too. Tap a glass off the gauge each time you pour, get nudged to check a bottle when it looks low, and keep a shopping list that fills itself.',
  },
  {
    title: 'Your household',
    text: 'Invite the people you live with and you share one collection. Your shelf and their library become the same catalogue; duplicates are merged.',
  },
  {
    title: 'Friends',
    text: 'See what friends have on their shelves and in their bar before you borrow, lend or bring a bottle. Your own shelf names stay private.',
  },
];

function GoogleButton() {
  return (
    <a className="google-button" href={signInUrl(returnPath())}>
      <svg className="google-button__logo" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
      <span>Sign in with Google</span>
    </a>
  );
}

/** What visitors see before signing in: what the app is, then the one way in. */
export function SignInPage() {
  const error = signInError();

  return (
    <main className="landing">
      <section className="landing__hero">
        <img className="sign-in__mark" src="/favicon.svg" alt="" />
        <h1>My Catalogue</h1>
        <p className="landing__lead">
          Everything on your shelves and in your bar, catalogued with your phone's camera, shared with the people you live
          with.
        </p>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <GoogleButton />
        <p className="sign-in__note">Invite-only: your email has to be on this server's list. Your data stays on it.</p>
      </section>

      <ul className="landing__features">
        {FEATURES.map((feature) => (
          <li key={feature.title} className="landing__feature">
            <h2>{feature.title}</h2>
            <p>{feature.text}</p>
          </li>
        ))}
      </ul>

      <section className="landing__how">
        <h2>How it works</h2>
        <ol className="first-run__steps">
          <li>
            <span>
              <strong>Scan a barcode.</strong> Books are looked up by ISBN, drinks by their product barcode. No camera? Type
              the number, or add it by hand.
            </span>
          </li>
          <li>
            <span>
              <strong>Check and save.</strong> Fix anything the lookup got wrong, add the vintage or which shelf it lives on.
            </span>
          </li>
          <li>
            <span>
              <strong>Find it later.</strong> Search from the shop to see if you already have it, and how much is left.
            </span>
          </li>
        </ol>
        <GoogleButton />
      </section>
    </main>
  );
}
